import React, { ChangeEvent, FC, useRef, useState } from 'react'

import clsx from 'clsx'
import { AutorenewRounded } from '@mui/icons-material'

import { IconFile } from '../../../../icons/IconFile'

import FirebaseStorageService from '../../../../../services/storageService'

import FirestoreService from '../../../../../services/firestoreService'
import helpers from '../../../../../commonHelpers/helpers'
import useToasterHelper from '../../../../../helpers/ToasterHelper'
import { handleImageError } from '../../../../../helpers/handleImageError'
import { generateName } from '../../../../../helpers/helpers'

import { RegistrationByDayModel } from '../../../../../models/registrations-by-day/registrationByDay.model'

import { IRegistrationByDayInterface } from '../../../../../models/registrations-by-day/registrationByDay.interface'

import { useAppSelector } from '../../../../../store/hooks'
import { selectUserId } from '../../../../../store/user/userSlice'

import { IMAGE_CONSTS } from '../../../../../const/image-const'
import { MESSAGES_CONST } from '../../../../../const/messages-const'
import { CONST } from '../../../../../const/const'

interface ScoringRiderInfoProps {
  row: IRegistrationByDayInterface
  setRow: (data: IRegistrationByDayInterface) => void
  file: string | null
  setFile: (value: string | null) => void
  editable: boolean
  className?: string
}
export const ScoringRiderInfo: FC<ScoringRiderInfoProps> = ({
  row,
  setRow,
  file,
  setFile,
  editable,
  className,
}) => {
  const toastFunctions = useToasterHelper()

  const userId = useAppSelector(selectUserId)

  const fileInputRef = useRef(null)

  const [uploadLoading, setUploadLoading] = useState<boolean>(false)

  async function fileChangedHandler(event: ChangeEvent<HTMLInputElement>) {
    const files = event.target.files
    const file = files?.[0]

    if (!file) return toastFunctions.success({ message: MESSAGES_CONST.NO_FILE_SELECTED })

    setUploadLoading(true)

    const PATH = CONST.DATA.STORAGE.EVENTS.EVENT_SCORE.PREFIX

    try {
      const downloadUrl = await FirebaseStorageService.uploadFile(file, `${PATH}/${userId}`)

      const newData = new RegistrationByDayModel(row)

      newData.score = { ...newData.score, file: downloadUrl as string }
      setFile(downloadUrl as string)

      await FirestoreService.updateItem(
        CONST.DATA.FIRESTORE.LATEST.COLLECTIONS.REGISTRATION_BY_DAY.NAME,
        newData.id,
        newData.toFirestore()
      )
      setRow(newData)

      toastFunctions.success({ message: MESSAGES_CONST.EVENT_SCORE_UPLOAD })
    } catch (error) {
      toastFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
      helpers.logger({
        isError: true,
        message: error,
      })
    } finally {
      setUploadLoading(false)
    }
  }

  return (
    <div className={clsx('flex justify-between mb-6', className)}>
      <div>
        <div className="flex gap-3 mb-2">
          <img
            className="w-[45px] h-[45px] rounded-lg object-cover"
            src={
              row?.riderProfilePicture ? row.riderProfilePicture : IMAGE_CONSTS.PLACEHOLDERS.USER
            }
            alt=""
          />
          <div>
            <div className="opacity-50 text-sm">Rider</div>
            <div>{row?.riderName}</div>
          </div>
        </div>

        {/* Horse Info */}
        <div className="flex gap-3">
          <img
            className="w-[45px] h-[45px] rounded-lg object-cover"
            src={row?.horseProfilePicture ?? ''}
            onError={(e) => handleImageError(e, generateName(row?.horseName || ''))}
            alt=""
          />
          <div>
            <div className="opacity-50 text-sm">Horse</div>
            <div>{row?.horseName ?? 'N/A'}</div>
          </div>
        </div>
      </div>

      <div className="flex flex-col items-end justify-between">
        <div className="flex items-center gap-2">
          <div className="text-right items-center">
            <div className="text-xs opacity-50">Back Number</div>
            <span>{Number(row.backNumber) > 0 ? row.backNumber : 'N/A'}</span>
          </div>
          <div className="flex items-center justify-center w-[45px] h-[45px] rounded-md bg-SeabiscuitMainThemeColor/5">
            <img src="/assets/og_icons/Shortlist-2.svg" className="w-6" alt="" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right items-center">
            <div className="text-xs opacity-50">File</div>
            <div>
              <label
                className={clsx(
                  (editable || file) && 'underline cursor-pointer hover:no-underline'
                )}
                onClick={(e) => {
                  if (file) {
                    if (!editable) {
                      e.preventDefault()
                      window.open(file, '_blank')
                    }
                  }
                }}
              >
                {uploadLoading ? (
                  <AutorenewRounded fontSize="small" className="animate-spin mx-auto" />
                ) : file ? (
                  editable ? (
                    'Update File'
                  ) : (
                    'View File'
                  )
                ) : editable ? (
                  'Add File'
                ) : (
                  'N/A'
                )}
                {editable && !uploadLoading && (
                  <input
                    id="fileInput"
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    ref={fileInputRef}
                    onChange={(e) => fileChangedHandler(e)}
                  />
                )}
              </label>
            </div>
          </div>

          <div className="flex items-center justify-center w-[45px] h-[45px] rounded-md bg-SeabiscuitMainThemeColor/5">
            <a target="_blank" href={file ?? ''} rel="noreferrer">
              <IconFile className="w-5" />
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}
